import React, { useMemo, useRef } from "react";
import { BaseLayer, mapStyle } from "../../../../../../../../Component/Map";
import {
  SelectedAppleFeature,
  AppleCountCalibrationLayer,
} from "../../../../../../../../Component/Map/Layers/apple-count/calibration/AppleCountCalibrationLayer";
import { TreeLayer } from "../../../../../../../../Component/Map/Layers/TreeLayer";
import { bbox } from "@turf/turf";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import {
  GeolocateControl,
  LngLatBoundsLike,
  Map as MapLibreMap,
  NavigationControl,
  ScaleControl,
  type MapRef,
} from "react-map-gl/maplibre";
import Popup from "../../../../../../../../Component/Map/Popup";
import { TreePositions } from "../../../../../../../../API/trees";
import { mapTransformRequest } from "../../../../../../../../API/AureaApi";
import { CalibratedTreeCount } from "../../../../../../../../Component/Map/Layers/apple-count/calibration/CalibratedTreeCount";
import { FruitCalibrationLegend } from "./CalibrationLegend";
import {
  AppleCountCalibrationFeatureCollection,
  CalibrationItem,
} from "../Calibration.types";
import { YieldBucket } from "../../../../../../../../Component/Map/Layers/apple-count/apple-count-helper";
import { ErrorAlert } from "../../../../../../../../Component/Error/ErrorAlert";
import { AppleCountFeatureProperties } from "../../../../../../../../Component/Map/Layers/layer.types";
import GeolocateCoachMark from "../components/GeolocateCoachMark/GeolocateCoachMark";

export function CalibrationMap({
  geojson,
  trees,
  state,
  actions,
  buckets,
}: {
  geojson: AppleCountCalibrationFeatureCollection;
  trees: TreePositions;
  state: {
    calibrationSet: CalibrationItem[];
    selectedFeatureId: number | null;
    lastCalibratedDate: string | undefined;
    loadingError: string;
  };
  buckets: YieldBucket[];
  actions: {
    onLoadingErrorClose: () => void;
    selectFeature: (feature: SelectedAppleFeature) => void;
  };
}) {
  const mapRef = useRef<MapRef | null>(null);
  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const controlOffset = isMobile ? "200px" : "10px";

  const {
    calibrationSet,
    selectedFeatureId,
    lastCalibratedDate,
    loadingError,
  } = state;
  const { onLoadingErrorClose, selectFeature: onSelectFeature } = actions;

  const savedPlotIds = useMemo(
    () => new Set(calibrationSet.map((s) => s.plotId)),
    [calibrationSet],
  );

  const selectFeature = (feature: SelectedAppleFeature) => {
    const map = mapRef.current;
    if (!map || !feature?.geometry) return;

    const bounds = bbox({
      type: "Feature",
      geometry: feature.geometry,
      properties: {},
    });

    map.fitBounds(bounds as LngLatBoundsLike, {
      duration: 1000,
      maxZoom: 20,
    });
    onSelectFeature(feature);
  };

  return (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        "& .maplibregl-ctrl-bottom-right": {
          bottom: `calc(${controlOffset} + 10px) !important`,
          transition: "bottom 0.3s ease",
        },
      }}
    >
      <MapLibreMap
        reuseMaps
        ref={mapRef}
        mapStyle={mapStyle}
        transformRequest={mapTransformRequest}
        initialViewState={{
          bounds: bbox(geojson) as LngLatBoundsLike,
        }}
        style={{ width: "100%", height: "100%" }}
      >
        <BaseLayer />

        {!!trees?.features?.length && <TreeLayer trees={trees} />}

        <AppleCountCalibrationLayer
          geojson={geojson}
          buckets={buckets}
          cutoff={{ min: 0, max: 999999 }}
          onSelectFeature={selectFeature}
          selectedFeatureId={selectedFeatureId ?? undefined}
          savedFeatureIds={savedPlotIds}
        />
        {lastCalibratedDate && (
          <Popup<AppleCountFeatureProperties>
            layer="apple-fill"
            component={CalibratedTreeCount}
          />
        )}
        <ScaleControl position="bottom-right" />
        <NavigationControl position="bottom-right" />
        <GeolocateControl
          position="bottom-right"
          trackUserLocation={true}
          showUserLocation={true}
          showAccuracyCircle={true}
          positionOptions={{
            enableHighAccuracy: true,
            timeout: 30000,
            maximumAge: 0,
          }}
          // Error codes:
          // 1 = PERMISSION_DENIED
          // 2 = POSITION_UNAVAILABLE
          // 3 = TIMEOUT
          onError={(e) => console.error("Geolocation error", e.code, e.message)}
        />
        <GeolocateCoachMark />
        {!isMobile && <FruitCalibrationLegend />}
      </MapLibreMap>
      {loadingError && (
        <ErrorAlert onClose={onLoadingErrorClose} text={loadingError} />
      )}
    </Box>
  );
}
